import { useState } from 'react';
import { MicButton, appendSpoken } from '@/components/ui/MicButton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Send } from 'lucide-react';
import { api } from '@/api/client';

type Remark = {
  id: number;
  orderId: string;
  text: string;
  isInternal: boolean;
  category?: string | null;
  authorName?: string | null;
  user?: { id: number; firstName: string; lastName?: string; userType: string } | null;
  createdAt: string;
};

const CATS = ['INFO', 'CALL_BACK', 'ESCALATION', 'PICKUP', 'DELIVERY', 'COD', 'NDR'];

export function OrderRemarks({ orderId }: { orderId: string | number }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [category, setCategory] = useState('INFO');
  const [isInternal, setIsInternal] = useState(true);

  const { data: remarks = [], isLoading } = useQuery({
    queryKey: ['order-remarks', String(orderId)],
    queryFn: async () => (await api.get<Remark[]>(`/orders/${orderId}/remarks`)).data,
  });

  const add = useMutation({
    mutationFn: async () => (await api.post(`/orders/${orderId}/remarks`, { text, isInternal, category })).data,
    onSuccess: () => {
      setText('');
      qc.invalidateQueries({ queryKey: ['order-remarks', String(orderId)] });
    },
  });

  return (
    <div className="border rounded-lg bg-white p-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare size={16} className="text-emerald-700" />
        <h3 className="font-semibold text-slate-900">Remarks / Internal Notes</h3>
        <span className="text-xs text-gray-400">({(remarks as Remark[]).length} entries)</span>
      </div>
      {/* Add */}
      <div className="flex gap-2 items-stretch mb-3">
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
          {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a remark / note — visible to internal team only"
          className="flex-1 border rounded px-2 py-1.5 text-sm"
          onKeyDown={(e) => { if (e.key === 'Enter' && text) add.mutate(); }}
        />
        <MicButton onText={(t) => setText((p: string) => appendSpoken(p, t))} />
        <label className="flex items-center gap-1 text-xs text-gray-600">
          <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} /> Internal
        </label>
        <button
          disabled={!text || add.isPending}
          onClick={() => add.mutate()}
          className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1 disabled:opacity-50"
        >
          <Send size={14} /> {add.isPending ? '…' : 'Add'}
        </button>
      </div>
      {/* Timeline */}
      {isLoading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : (remarks as Remark[]).length === 0 ? (
        <div className="text-gray-400 text-sm">No remarks yet</div>
      ) : (
        <div className="space-y-2">
          {(remarks as Remark[]).map((r) => (
            <div key={r.id} className="flex items-start gap-3 p-2 rounded border border-slate-100 bg-slate-50">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs">
                {(r.user?.firstName || r.authorName || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="font-medium text-slate-700">{r.user ? `${r.user.firstName} ${r.user.lastName ?? ''}` : r.authorName ?? 'System'}</span>
                  {r.user?.userType && <span className="text-gray-400">· {r.user.userType.replace(/_/g, ' ')}</span>}
                  {r.category && <span className="bg-emerald-100 text-emerald-700 px-1.5 rounded text-[10px]">{r.category}</span>}
                  {r.isInternal && <span className="bg-amber-100 text-amber-700 px-1.5 rounded text-[10px]">Internal</span>}
                  <span className="ml-auto">{new Date(r.createdAt).toLocaleString()}</span>
                </div>
                <div className="text-sm text-slate-800 mt-0.5">{r.text}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
